import { FC } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Table from '@components/table';

import { useTranslation } from 'react-i18next';
import { apiScheduleService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { getBillingStatusList, getFrequencyList, getNextPaymentDate } from '@utils/billing';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { useApiMutation } from '@hooks/api/useApiMutation';

import { EScheduleFrequency, EScheduleStatus, TSchedule } from '@xeppt/xeppt-sdk/types/schedule';
import Input from '@components/common/input';
import { formatDate, getScheduleStatusVariant } from '@utils/index';
import { enumRecipientTranslate } from '@locales/index';
import { ETransactionAction, ETransactionMethod } from '@xeppt/xeppt-sdk/types/transaction';
import Dropdown from '@components/common/dropdown';
import Status from '@components/common/status';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import useModalContext from '@hooks/context/useModalContext';
import { modalIds } from '@const/modals';
import emptyIllustration from '@svg/illustrations/empty_schedule_bill.svg';
import Tabs from '@components/tabs';
import Skeleton from '@components/common/skeleton';
import Info from '@components/common/info';
import { Icon } from '@components/icons';

interface IProps {
    onEdit: (data: TSchedule) => void;
    onSearch: (value: string) => void;
    refetchPaymentsList: () => void;
    search: string;
    activeTab: string;
    isLoading?: boolean;
    onActiveTabChange: (value: string) => void;
    paymentsList: TSchedule[];
    filter: {
        frequency?: EScheduleFrequency[];
        status?: EScheduleStatus[];
        method?: ETransactionMethod[];
        action?: ETransactionAction[];
    };
    onFilterChange: (val: {
        paymentFrequency?: EScheduleFrequency[];
        status?: EScheduleStatus[];
        method?: ETransactionMethod[];
        action?: ETransactionAction[];
    }) => void;
}

const PaymentsTable: FC<IProps> = ({
    paymentsList,
    refetchPaymentsList,
    onEdit,
    onSearch,
    filter,
    search,
    activeTab,
    onActiveTabChange,
    onFilterChange,
    isLoading
}) => {
    const navigate = useNavigate();
    const { onOpen, changeModalData, onClose } = useModalContext();
    const { t, i18n } = useTranslation('sections', {
        keyPrefix: 'send_and_request.schedule_payments.table'
    });
    const { placeholderLocale } = useLocales();
    const { t: frequencyLocale } = useTranslation('sections', {
        keyPrefix: 'send_and_request.schedule_payments.payment_details'
    });
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();

    const { handleRequest: handleCancelPayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deleteSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('cancel_schedule');
            refetchPaymentsList();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleRetrySchedule } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.retrySchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('retry_schedule');
            refetchPaymentsList();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDeactivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deactivateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('pause_schedule');
            refetchPaymentsList();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleActivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.activateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('activate_schedule');
            refetchPaymentsList();
        },
        onError: requestErrorLocale
    });

    const handlePauseResumePayment = (id: string, status: EScheduleStatus) => {
        if (status === EScheduleStatus.ACTIVE) {
            handleDeactivatePayment(id);
        } else {
            handleActivatePayment(id);
        }
    };

    const handleOpenCancelModal = (schedule: TSchedule) => {
        changeModalData({
            name: schedule.info.split('(')?.[0] || schedule.info
        });
        onOpen({
            modalId: modalIds.CANCEL_SCHEDULE,
            onSubmit: () => handleCancelPayment(schedule.id)
        });
    };

    const columns = [
        {
            label: t('name'),
            key: 'info',
            width: 300,
            render: (_: string, data: TSchedule) => (
                <div className={styles.recipient}>
                    {data.status === EScheduleStatus.ERROR && (
                        <Info variant="right" content="Last payment attempt failed">
                            <Icon name="warning" />
                        </Info>
                    )}
                    {data.info}
                </div>
            )
        },
        {
            label: t('frequency'),
            key: 'frequency',
            render: (value: EScheduleFrequency) =>
                getFrequencyList(frequencyLocale).find((item) => item.value === value)?.label,
            filterOptions: getFrequencyList(frequencyLocale)
        },
        {
            label: 'Recipient',
            key: 'method',
            render: (_: string, data: TSchedule) =>
                enumRecipientTranslate[data.method][i18n.language as 'en' | 'fr'],
            filterOptions: [
                { label: 'XEPPT contact', value: ETransactionMethod.WALLET },
                { label: 'Bank account', value: ETransactionMethod.EFT }
                // TEMP-HIDE EPS (founder 2026-06-29) — restore to re-enable: uncomment below
                // { label: 'Card', value: ETransactionMethod.EPS }
            ]
        },
        {
            label: t('status'),
            key: 'status',
            render: (value: EScheduleStatus) => (
                <Status variant={getScheduleStatusVariant(value)}>
                    {
                        getBillingStatusList(frequencyLocale).find((item) => item.value === value)
                            ?.label
                    }
                </Status>
            ),
            filterOptions: getBillingStatusList(frequencyLocale)
        },
        {
            label: t('amount'),
            key: 'amount',
            render: (value: number) => `$${value.toFixed(2)}`
        },
        {
            label: activeTab === 'payments' ? 'Next payment' : 'Next request',
            key: 'next_payment_at',
            render: (value: string, schedule: TSchedule) =>
                schedule.status !== EScheduleStatus.ACTIVE
                    ? '-'
                    : formatDate(
                          value ||
                              getNextPaymentDate({
                                  frequency: schedule.frequency,
                                  nextPaymentAt: schedule.next_payment_at,
                                  lastPaymentAt: schedule.last_payment_at
                              }),
                          'MM/DD/YYYY'
                      )
        },
        {
            label: 'Payments',
            key: 'remaining_payments',
            render: (value: number, data: TSchedule) =>
                value < 0 ? 'Indefinite' : value + data.total_payments
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_: undefined, data: TSchedule) => (
                <div className={styles.table_actions}>
                    <Dropdown
                        items={[
                            {
                                label: submitLocale('view_details'),
                                icon: 'open_eye',
                                onClick: () => {
                                    changeModalData({
                                        schedule: data,
                                        refetchSchedules: refetchPaymentsList,
                                        onEdit: (schedule: TSchedule) => {
                                            onClose();
                                            onEdit(schedule);
                                        }
                                    });
                                    onOpen({
                                        modalId: modalIds.VIEW_SCHEDULE
                                    });
                                }
                            },
                            (data.status === EScheduleStatus.ACTIVE ||
                                data.status === EScheduleStatus.INACTIVE) && {
                                label:
                                    data.status === EScheduleStatus.ACTIVE
                                        ? submitLocale('pause')
                                        : submitLocale('continue'),
                                icon: data.status === EScheduleStatus.ACTIVE ? 'pause' : 'restart',
                                onClick: () => handlePauseResumePayment(data.id, data.status)
                            },
                            data.status !== EScheduleStatus.COMPLETE && {
                                label: submitLocale('edit'),
                                icon: 'edit',
                                onClick: () => onEdit(data)
                            },
                            data.status === EScheduleStatus.ERROR && {
                                label: submitLocale('retry'),
                                icon: 'refresh',
                                onClick: () => handleRetrySchedule(data.id)
                            },
                            {
                                label: submitLocale('cancel'),
                                isRed: true,
                                icon:
                                    data.status === EScheduleStatus.ERROR
                                        ? 'trash'
                                        : 'close_outlined',
                                onClick: () => handleOpenCancelModal(data)
                            }
                        ]}
                    />
                </div>
            )
        }
    ];

    return (
        <motion.div {...pageAnimation} className={styles.content}>
            <div className={styles.content}>
                <Tabs
                    onChange={(value) => onActiveTabChange(value)}
                    tabs={[
                        { label: 'Payments', value: 'payments' },
                        { label: 'Money requests', value: 'requests' }
                    ]}
                    activeTab={activeTab}
                />
                <div className={styles.search_wrapper}>
                    <Input
                        value={search}
                        onChange={(val) => onSearch(val)}
                        full
                        className={styles.search}
                        leftIcon="search"
                        rightIcon={search.length > 0 ? 'filed_close' : undefined}
                        onClickRightIcon={() => onSearch('')}
                        placeholder={placeholderLocale('search_by_recipient')}
                    />
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={() => navigate(routes.send_and_request)}>
                        {submitLocale('schedule_new')}
                    </Button>
                </div>
            </div>
            {isLoading ? (
                <>
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                </>
            ) : paymentsList.length === 0 &&
              filter?.method?.length !== 0 &&
              filter?.frequency?.length !== 0 &&
              filter?.status?.length !== 0 ? (
                <div className={styles.empty}>
                    <img src={emptyIllustration} alt="empty payees" />
                    <p className={styles.title}>No payments have been scheduled yet</p>
                    <p className={styles.description}>
                        Payments you have scheduled will appear here
                    </p>
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={() => navigate(routes.send_and_request)}>
                        {submitLocale('schedule_payment')}
                    </Button>
                </div>
            ) : (
                <Table
                    //@ts-ignore
                    columns={columns}
                    rows={paymentsList}
                    className={styles.table}
                    isActions
                    //@ts-ignore
                    filter={filter}
                    onFilterChange={(val) => {
                        onFilterChange({
                            ...filter,
                            ...val
                        });
                    }}
                />
            )}
            {paymentsList.length === 0 &&
                filter?.frequency?.length === 0 &&
                filter?.method?.length === 0 &&
                filter?.status?.length === 0 &&
                !isLoading && (
                    <div className={styles.empty}>
                        <img src={emptyIllustration} alt="empty payees" />
                        <p className={styles.title}>No payments have been scheduled yet</p>
                        <p className={styles.description}>
                            Payments you have scheduled will appear here
                        </p>
                        <Button
                            variant="primary"
                            size="medium"
                            onClick={() => navigate(routes.send_and_request)}>
                            {submitLocale('schedule_payment')}
                        </Button>
                    </div>
                )}
        </motion.div>
    );
};

export default PaymentsTable;
